import FeaturePageDownloadButtons from 'components/features-page/FeaturePageDownloadButtons'
import TitleAndSubtitle from 'components/UI/TitleAndSubtitle'
import { graphql, useStaticQuery } from 'gatsby'
import DesktopApp from 'images/desktop-app-desktop.png'
import MacosApp from 'images/desktop-app-macos.png'
import ChromeApp from 'images/windscribe-chrome-extension.png'
import EdgeApp from 'images/windscribe-edge-extension.png'
import FirefoxApp from 'images/windscribe-firefox-extension.png'
import { Flex, Grid } from 'theme-ui'
import getEnglishStrings from 'utils/getEnglishStrings'
import windscribeApp from 'images/windscribe-app.png'

type Header = {
  title: string
  subtitle: string
  image: string
}

export default function FeaturePageHero({ path }: { path: string }): JSX.Element {
  const data = useStaticQuery<Queries.FeaturePageHeroQuery>(graphql`
    query FeaturePageHero {
      directus {
        desktop_page {
          strings {
            languages_code {
              code
            }
            windows_title
            windows_subtitle
            linux_title
            linux_subtitle
            macos_title
            macos_subtitle
          }
        }
        browser_page {
          strings {
            languages_code {
              code
            }
            chrome_title
            chrome_subtitle
            firefox_title
            firefox_subtitle
            edge_title
            edge_subtitle
          }
        }
        mobile_page {
          strings {
            languages_code {
              code
            }
            ios_title
            ios_subtitle
            android_title
            android_subtitle
          }
        }
      }
    }
  `)

  const desktopFeaturesData = getEnglishStrings(data.directus?.desktop_page?.strings)
  const browserFeaturesData = getEnglishStrings(data.directus.browser_page?.strings)
  const mobileFeaturesData = getEnglishStrings(data.directus.mobile_page?.strings)

  const titleAndSubtitleRecord: Record<string, Header> = {
    '/features/windows/': {
      title: desktopFeaturesData?.windows_title ?? '',
      subtitle: desktopFeaturesData?.windows_subtitle ?? '',
      image: DesktopApp,
    },
    '/features/macos/': {
      title: desktopFeaturesData?.macos_title ?? '',
      subtitle: desktopFeaturesData?.macos_subtitle ?? '',
      image: MacosApp,
    },
    '/features/linux/': {
      title: desktopFeaturesData?.linux_title ?? '',
      subtitle: desktopFeaturesData?.linux_subtitle ?? '',
      image: DesktopApp,
    },
    '/features/chrome/': {
      title: browserFeaturesData?.chrome_title ?? '',
      subtitle: browserFeaturesData?.chrome_subtitle ?? '',
      image: ChromeApp,
    },
    '/features/firefox/': {
      title: browserFeaturesData?.firefox_title ?? '',
      subtitle: browserFeaturesData?.firefox_subtitle ?? '',
      image: FirefoxApp,
    },
    '/features/edge/': {
      title: browserFeaturesData?.edge_title ?? '',
      subtitle: browserFeaturesData?.edge_subtitle ?? '',
      image: EdgeApp,
    },
    '/features/ios/': {
      title: mobileFeaturesData?.ios_title ?? '',
      subtitle: mobileFeaturesData?.ios_subtitle ?? '',
      image: windscribeApp,
    },
    '/features/android/': {
      title: mobileFeaturesData?.android_title ?? '',
      subtitle: mobileFeaturesData?.android_subtitle ?? '',
      image: windscribeApp,
    },
  }

  return (
    <>
      <Grid
        columns={[1, 2, 2]}
        sx={{
          width: '100%',
          maxWidth: '124.8rem',
          px: '2.4rem',
          alignItems: 'center',
          gap: '4.8rem',
          mb: [0, '5.6rem'],
        }}
      >
        <Flex
          sx={{
            flexDirection: 'column',
            alignItems: ['center', 'start'],
            gap: '2.4rem',
          }}
        >
          <TitleAndSubtitle
            title={titleAndSubtitleRecord[path]?.title}
            subtitle={titleAndSubtitleRecord[path]?.subtitle}
            dataTestId={`${path}-features-title`}
            sxx={{ textAlign: ['center', 'start'] }}
          />
          <FeaturePageDownloadButtons path={path} />
        </Flex>
        <Flex sx={{ justifyContent: 'flex-end' }}>
          <img
            src={titleAndSubtitleRecord[path]?.image}
            alt="Windscribe extension app"
            sx={{
              width: '100%',
              maxWidth: ['100%', '50rem'],
              display: ['none', 'block', 'block'],
            }}
          />
        </Flex>
      </Grid>
      {[
        '/features/chrome/',
        '/features/firefox/',
        '/features/edge/',
        '/features/ios/',
        '/features/android/',
      ].includes(path) && (
        <div
          sx={{
            width: '100%',
            overflow: 'hidden',
            display: ['block', 'none', 'none'],
          }}
        >
          <img
            src={titleAndSubtitleRecord[path]?.image}
            alt=""
            sx={{ width: '100%', height: 'auto', overflow: 'hidden', mt: '4.8rem' }}
          />
        </div>
      )}
    </>
  )
}
